// Manage Collaborators Component
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, Trash2, Mail, Clock, CheckCircle, Loader2 } from 'lucide-react';

export function ManageCollaborators({ documentId }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    loadCollaborators();
  }, [documentId]);

  const loadCollaborators = async () => {
    try {
      const response = await api.request(`/collaborators/document/${documentId}`);
      setCollaborators(response.collaborators || []);
      setIsOwner(response.is_owner);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (collaboratorId) => {
    if (!window.confirm('Are you sure you want to remove this collaborator?')) {
      return;
    }

    setRemoving(collaboratorId);
    setError('');
    setSuccess('');

    try {
      const response = await api.request(`/collaborators/${collaboratorId}`, {
        method: 'DELETE'
      });
      
      console.log('Delete response:', response);
      
      // Immediately filter out the removed collaborator from state
      setCollaborators(prev => prev.filter(c => c.id !== collaboratorId));
      
      // Show success message
      setSuccess('Collaborator removed successfully');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to remove collaborator');
      // Reload to get fresh data on error
      await loadCollaborators();
    } finally {
      setRemoving(null);
    }
  };

  const handleRoleChange = async (collaboratorId, newRole) => {
    setUpdatingRole(collaboratorId);
    setError('');
    setSuccess('');

    try {
      await api.request(`/collaborators/${collaboratorId}/role?new_role=${newRole}`, {
        method: 'PATCH'
      });
      
      // Update local state
      setCollaborators(prev => prev.map(c => 
        c.id === collaboratorId ? { ...c, role: newRole } : c
      ));
      
      setSuccess(`Role updated to ${newRole}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update role');
      await loadCollaborators(); // Reload on error
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleResend = async (collab) => {
    if (!confirm(`Resend invitation to ${collab.user_email}?\n\nThis will send a new invitation email with a fresh link.`)) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      // First, remove the existing pending invitation
      await api.request(`/collaborators/${collab.id}`, {
        method: 'DELETE'
      });
      
      // Then send a new invitation
      await api.request(`/collaborators/invite`, {
        method: 'POST',
        body: JSON.stringify({
          document_id: documentId,
          invitee_email: collab.user_email,
          invitee_name: collab.user_name,
          role: collab.role
        })
      });
      
      // Reload collaborators list to show the new invitation
      await loadCollaborators();
      
      setSuccess('Invitation resent successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to resend invitation');
      // Reload on error to ensure UI is in sync
      await loadCollaborators();
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      editor: 'bg-blue-100 text-blue-800',
      viewer: 'bg-green-100 text-green-800',
      owner: 'bg-purple-100 text-purple-800'
    };
    return colors[role] || colors.viewer;
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      editor: 'Can edit & comment',
      viewer: 'Can view & comment',
      owner: 'Full access'
    };
    return descriptions[role] || 'Unknown';
  };

  const getStatusIcon = (status) => {
    if (status === 'active') {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    return <Clock className="h-4 w-4 text-yellow-600" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="animate-spin h-6 w-6 mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">Loading collaborators...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Collaborators ({collaborators.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {collaborators.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No collaborators yet</p>
            <p className="text-sm">Invite someone to start collaborating!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {collaborators.map((collab) => (
              <div
                key={collab.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 font-semibold">
                    {collab.user_name ? collab.user_name.charAt(0).toUpperCase() : collab.user_email.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {collab.user_name || collab.user_email}
                      </p>
                      {getStatusIcon(collab.status)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Mail className="h-3 w-3" />
                      {collab.user_email}
                      {!collab.user_name && collab.status === 'pending' && (
                        <span className="text-xs text-gray-400">(Not yet signed up)</span>
                      )}
                    </div>
                    {collab.accepted_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Joined {new Date(collab.accepted_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isOwner ? (
                    <Select
                      value={collab.role}
                      onValueChange={(value) => handleRoleChange(collab.id, value)}
                      disabled={updatingRole === collab.id}
                    >
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">
                          <span className="text-green-700">👁️ Viewer</span>
                        </SelectItem>
                        <SelectItem value="editor">
                          <span className="text-blue-700">✏️ Editor</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span 
                      className={`px-2 py-1 rounded text-xs font-semibold ${getRoleBadge(collab.role)}`}
                      title={getRoleDescription(collab.role)}
                    >
                      {collab.role}
                    </span>
                  )}
                  
                  {collab.status === 'pending' && (
                    <>
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                        Pending
                      </span>
                      {isOwner && (
                        <Button
                          onClick={() => handleResend(collab)}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          title="Resend invitation email"
                        >
                          Resend
                        </Button>
                      )}
                    </>
                  )}
                  {isOwner && (
                    <Button
                      onClick={() => handleRemove(collab.id)}
                      variant="ghost"
                      size="sm"
                      disabled={removing === collab.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {removing === collab.id ? (
                        <Loader2 className="animate-spin h-4 w-4" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
